/**
 * @fileoverview Helpdesk Detail Fields Configuration
 *
 * Configuration for editable fields on the helpdesk ticket detail page.
 * Mirrors the table picker config but adapted for detail view editing.
 *
 * Fields configured:
 * - Title (text input)
 * - Label (picker)
 * - Building (picker)
 * - Unit (picker)
 * - Source (picker)
 * - Reporter (picker)
 * - Supplier (picker with custom handler)
 * - Assignee (picker)
 * - Accounting Type (OPEX/CAPEX picker, standaard OPEX)
 * - Invoice Numbers (readonly, uit de gekoppelde OPEX-factuurregels)
 * - Invoice Total (readonly, som van de gekoppelde factuurregels)
 * - Estimated Cost (number input)
 * - Materials To Bring (textarea)
 * - Materials To Order (textarea)
 * - Solution Proposal (textarea)
 * - Actions Taken (textarea)
 * - Internal Analysis (textarea)
 *
 * @module Helpdesk/DetailFieldsConfig
 */

import { DetailFieldsConfig } from "shared/components/EditableDetailField/types";
import {
  Tag,
  Buildings,
  HouseLine,
  EnvelopeSimple,
  User,
  UserGear,
  PencilSimple,
  Package,
  CurrencyCircleDollar,
  Lightbulb,
  CheckCircle,
  FileText,
  ChartLineUp,
  ActivityIcon,
  Receipt,
  Coins,
} from "@phosphor-icons/react";
import { API_URL } from "constants/index";

/** Bedragen in de detailvelden consistent als EUR tonen. */
const formatEuro = (amount: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
  }).format(amount);

export const HELPDESK_DETAIL_FIELDS_CONFIG: DetailFieldsConfig = {
  entityName: "ticket",
  entityPrefix: "H",
  apiUpdateEndpoint: `${API_URL}/helpdesk/ticket/{id}`,

  fields: [
    // Title (text input)
    {
      id: "title",
      label: "Titel",
      type: "text",
      icon: PencilSimple,
      displayValue: (ticket) => ticket?.title || "-",
      extractValue: (ticket) => ticket?.title || "",
      buildUpdatePayload: (value) => ({ title: value }),
      placeholder: "Voer ticket titel in",
      validate: (value) => value.trim().length > 0,
      className: "col-span-full", // Take full width
      chipColor: "border-neutral-400 bg-neutral-50 text-neutral-700",
    },

    // Label (picker)
    {
      id: "label",
      label: "Label",
      type: "picker",
      icon: Tag,
      pickerMode: "helpdesk-label",
      displayValue: (ticket) => ticket?.label || "-",
      extractValue: (ticket) => ticket?.label,
      buildUpdatePayload: (value) => ({ label: value }),
      chipColor: "border-yellow-400 bg-yellow-50 text-yellow-700",
    },

    // Building (picker)
    {
      id: "building",
      label: "Gebouw",
      type: "picker",
      icon: Buildings,
      pickerMode: "building",
      displayValue: (ticket) => ticket?.building?.id || "-",
      extractValue: (ticket) => ticket?.building,
      buildUpdatePayload: (value) => ({
        building: value ? { id: value.id } : null,
      }),
      chipColor: "border-moss-primary/30 bg-moss-primary/10 text-moss-primary",
    },

    // Unit (picker - depends on building)
    {
      id: "unit",
      label: "Unit",
      type: "picker",
      icon: HouseLine,
      pickerMode: "unit",
      displayValue: (ticket) =>
        ticket?.unit?.id ? `Unit ${ticket.unit.id}` : "-",
      extractValue: (ticket) => ticket?.unit,
      extractContextValue: (ticket) => ticket?.building?.id, // Pass building for filtering
      buildUpdatePayload: (value) => ({
        unit: value ? { id: value.id } : null,
      }),
      chipColor: "border-moss-primary/30 bg-moss-primary/10 text-moss-primary",
    },

    // Source (picker)
    {
      id: "source",
      label: "Bron",
      type: "picker",
      icon: EnvelopeSimple,
      pickerMode: "source",
      displayValue: (ticket) => ticket?.source || "-",
      extractValue: (ticket) => ticket?.source,
      buildUpdatePayload: (value) => ({ source: value }),
      chipColor: "border-blue-400 bg-blue-50 text-blue-700",
    },

    // Internal Status / Phase (picker)
    {
      id: "internalStatus",
      label: "Status/Fase",
      type: "picker",
      icon: ActivityIcon,
      pickerMode: "helpdesk-status",
      displayValue: (ticket) => ticket?.internalStatus || "-",
      extractValue: (ticket) => ticket?.internalStatus,
      buildUpdatePayload: (value) => ({ internalStatus: value }),
      chipColor: "border-purple-400 bg-purple-50 text-purple-700",
    },

    // Reporter (picker)
    {
      id: "reporter",
      label: "Melder",
      type: "picker",
      icon: User,
      pickerMode: "reporter",
      displayValue: (ticket) => {
        if (!ticket?.reporter) return "-";
        const reporter = ticket.reporter;
        return `${reporter.firstName || ""} ${reporter.lastName || ""}`.trim();
      },
      extractValue: (ticket) => ticket?.reporter,
      buildUpdatePayload: (value) => ({
        reporter: value ? { id: value.id } : null,
      }),
      chipColor:
        "border-brick-primary/30 bg-brick-primary/10 text-brick-primary",
    },

    // NB: Leverancier(s) worden niet meer als generiek picker-veld getoond.
    // Een ticket kan meerdere leveranciers hebben; de detailpagina rendert
    // hiervoor de aparte <TicketSuppliersSection /> (multi-leverancier).

    // Assignee (picker)
    {
      id: "assignee",
      label: "Toegewezen",
      type: "picker",
      icon: UserGear,
      pickerMode: "assignee",
      displayValue: (ticket) => {
        if (!ticket?.assignedTo) return "-";
        const assignee = ticket.assignedTo;
        return `${assignee.first_name || ""} ${assignee.last_name || ""}`.trim();
      },
      extractValue: (ticket) =>
        ticket?.assignedTo ? { id: ticket.assignedTo } : null,
      buildUpdatePayload: (value) => ({ assignedTo: value?.id || null }),
      chipColor: "border-purple-400 bg-purple-50 text-purple-700",
    },

    // Accounting Type (OPEX/CAPEX picker)
    // Standaard OPEX: helpdeskwerk is onderhoud & herstellingen, CAPEX is de
    // uitzondering. 'Onbekend' is geen keuze meer — bestaande tickets zijn met
    // migratie 1785844800000 omgezet naar OPEX.
    {
      id: "accountingType",
      label: "Boekhoudtype",
      type: "picker",
      icon: ChartLineUp,
      pickerMode: "custom-tags",
      displayValue: (ticket) => ticket?.accountingType || "OPEX",
      extractValue: (ticket) => ticket?.accountingType || "OPEX",
      buildUpdatePayload: (value) => ({ accountingType: value }),
      chipColor: "border-blue-400 bg-blue-50 text-blue-700",
      customPickerConfig: {
        customOptions: ["OPEX", "CAPEX"],
      },
    },

    // Factuurnummers (readonly) — de OPEX-factuurregels die aan dit ticket
    // gekoppeld zijn. Koppelen/ontkoppelen gebeurt op de OPEX-facturenpagina of
    // via de sectie "Gekoppelde Facturen" hierboven; dit veld toont enkel.
    {
      id: "invoiceNumbers",
      label: "Factuurnummers",
      type: "readonly",
      icon: Receipt,
      displayValue: (ticket) =>
        ticket?.invoiceNumbers?.length
          ? ticket.invoiceNumbers.join(", ")
          : "-",
      extractValue: (ticket) => ticket?.invoiceNumbers ?? [],
      buildUpdatePayload: () => ({}),
      customDisplayRenderer: (ticket) => {
        const numbers: string[] = ticket?.invoiceNumbers ?? [];
        if (!numbers.length) {
          return (
            <span className="text-neutral-secondary-mid text-sm italic">
              Geen facturen gekoppeld
            </span>
          );
        }
        return (
          <div className="flex flex-wrap items-center justify-end gap-1">
            {numbers.map((invoiceNumber) => (
              <span
                key={invoiceNumber}
                className="rounded-md border border-blue-400 bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-700"
              >
                {invoiceNumber}
              </span>
            ))}
          </div>
        );
      },
    },

    // Factuurbedrag (readonly) — som van alle gekoppelde factuurregels, excl.
    // btw. Bewust apart van "Geschatte Kost": dat is de schatting vóór
    // uitvoering, dit is wat er effectief gefactureerd is.
    {
      id: "invoiceTotal",
      label: "Factuurbedrag",
      type: "readonly",
      icon: Coins,
      displayValue: (ticket) => {
        const total = Number(ticket?.actualCost ?? 0);
        if (!ticket?.invoiceLineCount || !Number.isFinite(total)) return "-";
        const lineCount = ticket.invoiceLineCount;
        return `${formatEuro(total)} (${lineCount} factuurlijn${lineCount === 1 ? "" : "en"})`;
      },
      extractValue: (ticket) => ticket?.actualCost ?? null,
      buildUpdatePayload: () => ({}),
      chipColor: "border-green-400 bg-green-50 text-green-700",
    },

    // Estimated Cost (number input via custom-tags)
    {
      id: "estimatedCost",
      label: "Geschatte Kost",
      type: "picker",
      icon: CurrencyCircleDollar,
      pickerMode: "number-input",
      displayValue: (ticket) => {
        if (!ticket?.estimatedCost) return "-";
        return formatEuro(ticket.estimatedCost);
      },
      extractValue: (ticket) => ticket?.estimatedCost || "",
      buildUpdatePayload: (value) => ({
        estimatedCost: parseFloat(value) || null,
      }),
      chipColor: "border-green-400 bg-green-50 text-green-700",
    },

    // Materials To Bring (textarea)
    {
      id: "materialsToBring",
      label: "Mee te brengen materialen",
      type: "textarea",
      icon: Package,
      displayValue: (ticket) => ticket?.materialsToBring || "-",
      extractValue: (ticket) => ticket?.materialsToBring || "",
      buildUpdatePayload: (value) => ({ materialsToBring: value }),
      placeholder: "Voer mee te brengen materialen in",
      chipColor: "border-orange-400 bg-orange-50 text-orange-700",
    },

    // Materials To Order (textarea)
    {
      id: "materialsToOrder",
      label: "Te bestellen materialen",
      type: "textarea",
      icon: Package,
      displayValue: (ticket) => ticket?.materialsToOrder || "-",
      extractValue: (ticket) => ticket?.materialsToOrder || "",
      buildUpdatePayload: (value) => ({ materialsToOrder: value }),
      placeholder: "Voer te bestellen materialen in",
      chipColor: "border-orange-400 bg-orange-50 text-orange-700",
    },

    // Solution Proposal (textarea)
    {
      id: "solutionProposal",
      label: "Oplossingsvoorstel",
      type: "textarea",
      icon: Lightbulb,
      displayValue: (ticket) => ticket?.solutionProposal || "-",
      extractValue: (ticket) => ticket?.solutionProposal || "",
      buildUpdatePayload: (value) => ({ solutionProposal: value }),
      placeholder: "Voer oplossingsvoorstel in",
      chipColor: "border-yellow-400 bg-yellow-50 text-yellow-700",
    },

    // Actions Taken (textarea)
    {
      id: "actionsTaken",
      label: "Ondernomen acties",
      type: "textarea",
      icon: CheckCircle,
      displayValue: (ticket) => ticket?.actionsTaken || "-",
      extractValue: (ticket) => ticket?.actionsTaken || "",
      buildUpdatePayload: (value) => ({ actionsTaken: value }),
      placeholder: "Voer ondernomen acties in",
      chipColor: "border-green-400 bg-green-50 text-green-700",
    },

    // Internal Analysis (textarea)
    {
      id: "internalAnalysis",
      label: "Interne analyse",
      type: "textarea",
      icon: FileText,
      displayValue: (ticket) => ticket?.internalAnalysis || "-",
      extractValue: (ticket) => ticket?.internalAnalysis || "",
      buildUpdatePayload: (value) => ({ internalAnalysis: value }),
      placeholder: "Voer interne analyse in",
      chipColor: "border-indigo-400 bg-indigo-50 text-indigo-700",
    },
  ],
};

/**
 * Helper function to get a specific field config by ID
 */
export const getFieldConfig = (fieldId: string) => {
  return HELPDESK_DETAIL_FIELDS_CONFIG.fields.find((f) => f.id === fieldId);
};

/**
 * Helper function to get all editable fields (excluding readonly)
 */
export const getEditableFields = () => {
  return HELPDESK_DETAIL_FIELDS_CONFIG.fields.filter(
    (f) => f.type !== "readonly",
  );
};
